import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DEFAULT_PAGINATOR_STATE_V3 } from '../../../constants/constants';
import { CHEVRON_LEFT_ICON, CHEVRON_RIGHT_ICON } from '../../../constants/images';
import { CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE } from '../../../constants/tagManager';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';
import { GenericVoidFunction } from '../../../types/genericTypes';
import BdsIconButton from '../Buttons/BdsIconButton/BdsIconButton';
import DropdownButton from '../Buttons/DropdownButton/DropdownButton';

import './TablePagination.scss';

const ACTUAL_PAGE_CLASS = 'actual-page';

interface ITablePaginationProps {
  rowsPerPageOptions: number[];
  count: number;
  rowsPerPage: number;
  actualPage: number;
  marginTop?: number;
  sectionPage?: { section: string };
  onPageChange: GenericVoidFunction;
  onRowsPerPageChange: GenericVoidFunction;
}

const TablePaginationV3 = ({
  rowsPerPageOptions,
  count,
  rowsPerPage,
  actualPage,
  marginTop,
  sectionPage,
  onPageChange,
  onRowsPerPageChange
}: ITablePaginationProps) => {
  const [pages, setPages] = useState<number[]>([]);

  const { t } = useTranslation();

  const setPaginatorPages = useCallback(
    (dataLength: number, isInitial = false) => {
      const totalPages = Math.ceil(dataLength / rowsPerPage);
      const initialPages = [];
      for (let i = 0; i < totalPages; i++) {
        initialPages.push(i + 1);
      }
      setPages(initialPages);
      if (!isInitial) {
        onPageChange(0);
      }
    },
    [onPageChange, rowsPerPage]
  );

  useEffect(() => {
    setPaginatorPages(count, true);
  }, [count, setPaginatorPages, onPageChange]);

  const getFirstInPage = () => {
    return actualPage < 1 ? 1 : actualPage * rowsPerPage + 1;
  };

  const getLastInPage = () => {
    const aux = (actualPage + 1) * rowsPerPage;
    return count < aux ? count : aux;
  };

  const getLastPage = () => {
    const totalPages = Math.ceil(count / rowsPerPage);
    return totalPages - 1;
  };

  const onClickNextPage = () => {
    if (actualPage < getLastPage()) {
      const nextPage = actualPage + 1;
      onPageChange(nextPage);
      TagManagerService.pushEvent(CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE, {
        number: nextPage + 1,
        type: 'arrow',
        ...sectionPage
      });
    }
  };

  const onClickPreviousPage = () => {
    if (actualPage > 0) {
      const previousPage = actualPage - 1;
      onPageChange(previousPage);
      TagManagerService.pushEvent(CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE, {
        number: previousPage + 1,
        type: 'arrow',
        ...sectionPage
      });
    }
  };

  const renderPagePaginator = (index: number, value: number) => {
    const totalPages = Math.ceil(count / rowsPerPage);
    const clickableIndexPage = (
      <button
        className="page-numbers"
        onClick={() => {
          onPageChange(index);
          TagManagerService.pushEvent(CLICK_ACTION, PAGINATOR_JUMP_TO_PAGE, {
            number: index + 1,
            type: 'number',
            ...sectionPage
          });
        }}
      >
        <p className={'clickable ' + getActualPageClass(index)}>{value}</p>
      </button>
    );
    if (showIndex(index, totalPages)) {
      return clickableIndexPage;
    }
    if (Math.abs(actualPage - index) === 2) {
      return (
        <div className="index">
          <p>...</p>
        </div>
      );
    }
  };

  const showIndex = (index: number, totalPages: number) =>
    index === 0 ||
    index === totalPages - 1 ||
    index === actualPage ||
    Math.abs(actualPage - index) === 1 ||
    (Math.abs(actualPage - (totalPages - 1)) === 3 && Math.abs(index - (totalPages - 1)) === 1) ||
    (Math.abs(actualPage - 3) === 0 && Math.abs(index - 1) == 0);

  const getActualPageClass = (index: number) => {
    return index === actualPage ? ACTUAL_PAGE_CLASS : '';
  };

  if (count < DEFAULT_PAGINATOR_STATE_V3) return <></>;

  return (
    <div className="paginator-row-container" style={{ marginTop }}>
      <div className="paginator-row first">
        <p>
          {t(TRANSLATIONS_GLOBAL.paginator.rowsDetail, {
            pageFirst: getFirstInPage(),
            pageLast: getLastInPage(),
            total: count
          })}
        </p>
      </div>
      <div className="paginator-row center">
        <DropdownButton
          selectionTitle={t('global.paginator.rows_per_page')}
          defaultOption={rowsPerPage.toString()}
          options={rowsPerPageOptions.map((opt) => ({ name: opt.toString(), value: opt.toString() }))}
          onChange={(e) => onRowsPerPageChange(parseInt(e.value))}
        />
        <p>{t(TRANSLATIONS_GLOBAL.paginator.rowsPerPage)}</p>
      </div>
      <div className="paginator-row last">
        <div className="paginator">
          <BdsIconButton
            icon={CHEVRON_LEFT_ICON}
            variant="transparent"
            action={onClickPreviousPage}
            size="lg"
            label="lastPage"
            disabled={actualPage == 0}
          />
          {pages.map((value, index) => (
            <React.Fragment key={`page-${index}`}>{renderPagePaginator(index, value)}</React.Fragment>
          ))}
          <BdsIconButton
            icon={CHEVRON_RIGHT_ICON}
            variant="transparent"
            action={onClickNextPage}
            size="lg"
            label="nextPage"
            disabled={actualPage == getLastPage()}
          />
        </div>
      </div>
    </div>
  );
};

export default TablePaginationV3;
